import type { CarDefinition } from '../core/types';

/**
 * The display stats are deliberately kept on the ten point scale from the
 * design brief.  The physics values below are the real tuning values consumed
 * by the vehicle simulation; none of the cars are cosmetic reskins.
 */
export const CARS: readonly CarDefinition[] = [
  {
    id: 'apex',
    name: 'Apex',
    tagline: 'Confidence at every corner',
    description:
      'A composed all-round coupe with predictable grip, friendly steering and enough speed for every course.',
    stats: { acceleration: 7, topSpeed: 7, handling: 7, grip: 7 },
    physics: {
      acceleration: 25.5,
      reverseAcceleration: 12.5,
      brakePower: 38,
      maxSpeed: 70,
      maxReverseSpeed: 18,
      steeringRate: 2.28,
      highSpeedSteering: 0.48,
      lateralGrip: 8.2,
      handbrakeGrip: 2.45,
      rollingResistance: 0.44,
      aerodynamicDrag: 0.0023,
      downforce: 10.5,
      airControl: 1.05,
      stability: 8.5,
    },
    shape: {
      bodyLength: 4.35,
      bodyWidth: 1.88,
      bodyHeight: 0.66,
      cabinLength: 2.05,
      cabinWidth: 1.55,
      cabinHeight: 0.72,
      cabinOffset: -0.08,
      noseSlope: 0.42,
      wheelRadius: 0.4,
      wheelWidth: 0.29,
      spoiler: 'lip',
      archetype: 'coupe',
    },
    primaryColor: 0x28c7fa,
    secondaryColor: 0x11283d,
    accentColor: 0xeafaff,
  },
  {
    id: 'bolt',
    name: 'Bolt',
    tagline: 'Leave the corner first',
    description:
      'A featherweight hatch that launches hard and changes direction eagerly, trading composure and maximum speed for urgency.',
    stats: { acceleration: 10, topSpeed: 6, handling: 8, grip: 6 },
    physics: {
      acceleration: 32.5,
      reverseAcceleration: 14,
      brakePower: 40,
      maxSpeed: 64,
      maxReverseSpeed: 19,
      steeringRate: 2.62,
      highSpeedSteering: 0.5,
      lateralGrip: 7.1,
      handbrakeGrip: 2.05,
      rollingResistance: 0.37,
      aerodynamicDrag: 0.00265,
      downforce: 8.4,
      airControl: 1.28,
      stability: 6.8,
    },
    shape: {
      bodyLength: 3.72,
      bodyWidth: 1.76,
      bodyHeight: 0.72,
      cabinLength: 1.92,
      cabinWidth: 1.5,
      cabinHeight: 0.82,
      cabinOffset: 0.12,
      noseSlope: 0.26,
      wheelRadius: 0.37,
      wheelWidth: 0.26,
      spoiler: 'none',
      archetype: 'hatch',
    },
    primaryColor: 0xffc928,
    secondaryColor: 0x302510,
    accentColor: 0xfffbdf,
  },
  {
    id: 'vector',
    name: 'Vector',
    tagline: 'Velocity, distilled',
    description:
      'A long, low wedge built for huge straights. It rewards early turn-in and a steady hand when the road gets technical.',
    stats: { acceleration: 6, topSpeed: 10, handling: 5, grip: 6 },
    physics: {
      acceleration: 22,
      reverseAcceleration: 11.5,
      brakePower: 36,
      maxSpeed: 83,
      maxReverseSpeed: 18,
      steeringRate: 1.88,
      highSpeedSteering: 0.39,
      lateralGrip: 7.2,
      handbrakeGrip: 2.2,
      rollingResistance: 0.41,
      aerodynamicDrag: 0.00162,
      downforce: 12.2,
      airControl: 0.88,
      stability: 7.7,
    },
    shape: {
      bodyLength: 4.78,
      bodyWidth: 1.96,
      bodyHeight: 0.55,
      cabinLength: 1.78,
      cabinWidth: 1.48,
      cabinHeight: 0.58,
      cabinOffset: -0.18,
      noseSlope: 0.76,
      wheelRadius: 0.42,
      wheelWidth: 0.31,
      spoiler: 'lip',
      archetype: 'wedge',
    },
    primaryColor: 0xd934ff,
    secondaryColor: 0x1c1030,
    accentColor: 0x70efff,
  },
  {
    id: 'anchor',
    name: 'Anchor',
    tagline: 'Mass becomes momentum',
    description:
      'A broad, planted bruiser with exceptional lateral grip and landing stability. Slow to launch, difficult to unsettle.',
    stats: { acceleration: 5, topSpeed: 7, handling: 5, grip: 10 },
    physics: {
      acceleration: 19.5,
      reverseAcceleration: 10,
      brakePower: 43,
      maxSpeed: 69,
      maxReverseSpeed: 16,
      steeringRate: 1.84,
      highSpeedSteering: 0.45,
      lateralGrip: 11.6,
      handbrakeGrip: 3.15,
      rollingResistance: 0.56,
      aerodynamicDrag: 0.00225,
      downforce: 14.5,
      airControl: 0.7,
      stability: 12.5,
    },
    shape: {
      bodyLength: 4.62,
      bodyWidth: 2.08,
      bodyHeight: 0.78,
      cabinLength: 1.84,
      cabinWidth: 1.66,
      cabinHeight: 0.73,
      cabinOffset: -0.22,
      noseSlope: 0.18,
      wheelRadius: 0.45,
      wheelWidth: 0.36,
      spoiler: 'none',
      archetype: 'muscle',
    },
    primaryColor: 0xe94c3d,
    secondaryColor: 0x351513,
    accentColor: 0xffc247,
  },
  {
    id: 'flick',
    name: 'Flick',
    tagline: 'Put it exactly there',
    description:
      'A compact track special with razor-sharp response and strong grip, tuned to make switchbacks and hairpins feel effortless.',
    stats: { acceleration: 8, topSpeed: 6, handling: 10, grip: 8 },
    physics: {
      acceleration: 28,
      reverseAcceleration: 13.5,
      brakePower: 42,
      maxSpeed: 63,
      maxReverseSpeed: 19,
      steeringRate: 3.02,
      highSpeedSteering: 0.58,
      lateralGrip: 9.4,
      handbrakeGrip: 2.65,
      rollingResistance: 0.4,
      aerodynamicDrag: 0.00252,
      downforce: 11.6,
      airControl: 1.22,
      stability: 8.1,
    },
    shape: {
      bodyLength: 3.98,
      bodyWidth: 1.9,
      bodyHeight: 0.58,
      cabinLength: 1.58,
      cabinWidth: 1.52,
      cabinHeight: 0.64,
      cabinOffset: 0.02,
      noseSlope: 0.58,
      wheelRadius: 0.39,
      wheelWidth: 0.32,
      spoiler: 'wing',
      archetype: 'track',
    },
    primaryColor: 0x42e67b,
    secondaryColor: 0x102b20,
    accentColor: 0xf5ff65,
  },
  {
    id: 'havoc',
    name: 'Havoc',
    tagline: 'Fast, loud, sideways',
    description:
      'A rear-heavy expert machine with serious power and deliberately loose grip. Smooth corrections turn its chaos into speed.',
    stats: { acceleration: 8, topSpeed: 9, handling: 7, grip: 4 },
    physics: {
      acceleration: 29,
      reverseAcceleration: 13,
      brakePower: 37,
      maxSpeed: 78,
      maxReverseSpeed: 20,
      steeringRate: 2.4,
      highSpeedSteering: 0.46,
      lateralGrip: 5.1,
      handbrakeGrip: 1.35,
      rollingResistance: 0.34,
      aerodynamicDrag: 0.00186,
      downforce: 9.2,
      airControl: 1.12,
      stability: 5.2,
    },
    shape: {
      bodyLength: 4.45,
      bodyWidth: 2,
      bodyHeight: 0.64,
      cabinLength: 1.62,
      cabinWidth: 1.5,
      cabinHeight: 0.66,
      cabinOffset: -0.4,
      noseSlope: 0.5,
      wheelRadius: 0.43,
      wheelWidth: 0.35,
      spoiler: 'wing',
      archetype: 'rear-heavy',
    },
    primaryColor: 0xff6b1a,
    secondaryColor: 0x2b1110,
    accentColor: 0xd9f4ff,
  },
] as const;

export const CAR_DEFINITIONS = CARS;
export const DEFAULT_CAR_ID = 'apex';

export const CAR_BY_ID: Readonly<Record<string, CarDefinition>> = Object.freeze(
  Object.fromEntries(CARS.map((car) => [car.id, car])),
);

export function getCarDefinition(id: string): CarDefinition {
  return CAR_BY_ID[id] ?? CAR_BY_ID[DEFAULT_CAR_ID];
}

export const getCarById = getCarDefinition;
